import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import { format, isValid, parse } from "date-fns";
import {
  getOrganizationCustomer,
  listPartnerPrograms,
} from "../../../helpers/partnerCentral_helper";
import { getCustomerOrgAccounts } from "../../../store/customer/customerSlice";
import { formFromOrganizationCustomer } from "../../PartnerCentral/customerPayload";
import { programOptionLabel } from "../../PartnerCentral/WholesaleProgramsField";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  auditHubPageSx,
  outlinedButtonSx,
} from "../../Compliance/AuditHub/theme";
import {
  getCustomerAccountEntries,
  getCustomerAccountNumbers,
  isCustomerActive,
  isSubOrganization,
} from "../utils/customerHelpers";

/** Flatten GET customer-org-accounts payload to account row objects. */
const flattenOrgAccountEntries = (payload) => {
  if (!payload) return [];
  let data = payload?.data !== undefined ? payload.data : payload;
  if (
    data?.data &&
    typeof data.data === "object" &&
    !Array.isArray(data.data) &&
    data.account_number == null &&
    data.accountNumber == null
  ) {
    data = data.data;
  }
  if (!data || typeof data !== "object") return [];
  return (Array.isArray(data) ? data : Object.values(data)).filter(
    (acc) => acc && typeof acc === "object"
  );
};

const STATUS_TONE = {
  ACTIVE: { bg: "#ecfdf5", fg: "#047857" },
  DRAFT: { bg: "#fffbeb", fg: "#b45309" },
  RETIRED: { bg: "#eff6ff", fg: "#2563eb" },
  ARCHIVED: { bg: "#f1f5f9", fg: "#475569" },
};

const formatDate = (value) => {
  if (!value) return "—";
  const raw = String(value).trim();
  const iso = raw.slice(0, 10);
  const parsed = parse(iso, "yyyy-MM-dd", new Date());
  if (isValid(parsed)) return format(parsed, "MM/dd/yyyy");
  const local = new Date(raw.replace(/-/g, "/"));
  if (!Number.isNaN(local.getTime())) {
    return local.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  return raw || "—";
};

const display = (value) => {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const resolveOrganizationId = (customer) => {
  const raw =
    customer?.organization_id ?? customer?.organizationId ?? customer?.orgId;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : raw;
};

/** Normalize a created-by API value (string or { email|name|username }). */
const createdByText = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object") {
    const nested =
      value.email ??
      value.name ??
      value.username ??
      value.user_name ??
      value.userName ??
      value.display_name ??
      value.displayName;
    return nested == null ? "" : String(nested).trim();
  }
  return "";
};

/**
 * Read-only creator from org / Partner Central audit fields.
 * Backend may send snake_case, camelCase, or a nested identity object.
 */
const resolveCreatedBy = (...sources) => {
  const keys = [
    "createdBy",
    "created_by",
    "createdby",
    "CreatedBy",
    "created_by_name",
    "createdByName",
    "created_by_username",
    "createdByUsername",
    "created_by_email",
    "createdByEmail",
    "creator",
    "creator_email",
    "creatorEmail",
  ];
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    for (const key of keys) {
      const text = createdByText(source[key]);
      if (text) return text;
    }
  }
  return "";
};

const resolveCreatedDate = (...sources) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    const value =
      source.createdDate ??
      source.created_date ??
      source.created_at ??
      source.createdAt;
    if (value) return value;
  }
  return "";
};

const StatusChip = ({ status }) => {
  const tone = STATUS_TONE[status] || STATUS_TONE.ARCHIVED;
  return (
    <Chip
      size="small"
      label={status || "—"}
      sx={{
        height: 22,
        fontSize: "0.75rem",
        fontWeight: 600,
        borderRadius: "6px",
        backgroundColor: tone.bg,
        color: tone.fg,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

StatusChip.propTypes = {
  status: PropTypes.string,
};

/** Matches PayerPayeeAccountsField Selected Accounts chips (read-only). */
const selectedAccountChipSx = (isPayer) => ({
  height: 28,
  fontSize: 12,
  fontWeight: 500,
  backgroundColor: isPayer ? "#fff4eb" : AH_WHITE,
  border: `1px solid ${isPayer ? `${AH_ACCENT}55` : AH_BORDER}`,
  color: AH_TEXT,
  maxWidth: "100%",
  "& .MuiChip-label": {
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

/** Same Selected Accounts panel as Create/Edit AWS Payer / Payee field. */
const AccountChips = ({ items }) => {
  if (!items?.length) {
    return (
      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: AH_TEXT }}>
        —
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        border: `1px solid ${AH_BORDER}`,
        borderRadius: "8px",
        backgroundColor: AH_TRACK,
        p: { xs: 1.25, sm: 1.5 },
      }}
    >
      {items.map((item) => {
        const number = item.accountNumber;
        const isPayer = Boolean(item.isPayer);
        const name = String(item.accountName || "").trim();
        const titled = name ? `${number} · ${name}` : number;
        return (
          <Chip
            key={`${isPayer ? "payer" : "account"}-${number}`}
            size="small"
            label={isPayer ? `Payer: ${number}` : number}
            title={isPayer ? `Payer: ${titled}` : titled}
            sx={selectedAccountChipSx(isPayer)}
          />
        );
      })}
    </Box>
  );
};

AccountChips.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      accountNumber: PropTypes.string.isRequired,
      isPayer: PropTypes.bool,
      accountName: PropTypes.string,
    })
  ),
};

/**
 * Selected accounts only (same set as Create/Edit Selected Accounts) — never
 * inject payers that are not in the selection. Orange "Payer:" only when that
 * selected account is itself a payer (explicit form chip or self payer_account_id).
 */
const buildLinkedAccountItems = (customer, form, accountNumbers, orgAccountEntries = []) => {
  const numbers = [
    ...new Set(
      (accountNumbers || [])
        .map((id) => String(id || "").trim())
        .filter((id) => /^\d{12}$/.test(id))
    ),
  ];
  if (!numbers.length) return [];

  const numberSet = new Set(numbers);
  const payerSet = new Set();
  const nameByNumber = new Map();

  const markSelectedPayer = (id) => {
    const n = String(id || "").trim();
    if (/^\d{12}$/.test(n) && numberSet.has(n)) payerSet.add(n);
  };

  const rememberName = (number, name) => {
    const n = String(number || "").trim();
    const label = String(name || "").trim();
    if (!/^\d{12}$/.test(n) || !label || nameByNumber.has(n)) return;
    nameByNumber.set(n, label);
  };

  // Explicit Selected Accounts "Payer:" chips (Create/Edit) — only if still selected.
  (form?.payerAccountNumbers || []).forEach(markSelectedPayer);

  // account_payers / account entries: mark only when this selected account is a self-payer.
  const markSelfPayer = (accountNumber, payerAccountId, accountName) => {
    const number = String(accountNumber || "").trim();
    const payerId = String(payerAccountId || "").trim();
    rememberName(number, accountName);
    if (
      /^\d{12}$/.test(number) &&
      numberSet.has(number) &&
      payerId === number
    ) {
      payerSet.add(number);
    }
  };

  const rawPayers = customer?.account_payers;
  if (rawPayers && typeof rawPayers === "object" && !Array.isArray(rawPayers)) {
    Object.entries(rawPayers).forEach(([key, item]) => {
      if (!item || typeof item !== "object") return;
      markSelfPayer(
        item.account_number ?? item.accountNumber ?? key,
        item.payer_account_id ?? item.payerAccountId,
        item.account_name ?? item.accountName
      );
    });
  } else if (Array.isArray(rawPayers)) {
    rawPayers.forEach((item) => {
      if (!item || typeof item !== "object") return;
      markSelfPayer(
        item.account_number ?? item.accountNumber,
        item.payer_account_id ?? item.payerAccountId,
        item.account_name ?? item.accountName
      );
    });
  }

  getCustomerAccountEntries(customer).forEach((acc) => {
    markSelfPayer(acc?.accountNumber, acc?.payerAccountId, acc?.accountName);
  });

  (orgAccountEntries || []).forEach((acc) => {
    if (!acc || typeof acc !== "object") return;
    markSelfPayer(
      acc.account_number ?? acc.accountNumber,
      acc.payer_account_id ?? acc.payerAccountId,
      acc.account_name ?? acc.accountName
    );
  });

  // Keep selected-list order — do not pull payers to the front.
  return numbers.map((accountNumber) => ({
    accountNumber,
    isPayer: payerSet.has(accountNumber),
    accountName: nameByNumber.get(accountNumber) || "",
  }));
};

const DetailRow = ({ label, value, last }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "minmax(150px, 38%) 1fr" },
      gap: { xs: 0.35, sm: 2 },
      alignItems: "start",
      py: 1.25,
      borderBottom: last ? "none" : `1px solid ${AH_BORDER}`,
    }}
  >
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 600,
        color: AH_MUTED,
        lineHeight: 1.45,
        pt: { sm: 0.1 },
      }}
    >
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>
      {typeof value === "string" || typeof value === "number" ? (
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 500,
            color: AH_TEXT,
            lineHeight: 1.45,
            overflowWrap: "anywhere",
          }}
        >
          {value}
        </Typography>
      ) : (
        value
      )}
    </Box>
  </Box>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  last: PropTypes.bool,
};

const DetailSection = ({ title, rows }) => {
  if (!rows.length) return null;
  return (
    <Box
      sx={{
        border: `1px solid ${AH_BORDER}`,
        borderRadius: "10px",
        backgroundColor: AH_WHITE,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1,
          backgroundColor: AH_TRACK,
          borderBottom: `1px solid ${AH_BORDER}`,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.02em",
            color: AH_TEXT,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: 1.75 }}>
        {rows.map((row, index) => (
          <DetailRow
            key={row.label}
            label={row.label}
            value={row.value}
            last={index === rows.length - 1}
          />
        ))}
      </Box>
    </Box>
  );
};

DetailSection.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.node,
    })
  ).isRequired,
};

const pushIf = (rows, condition, item) => {
  if (condition) rows.push(item);
};

const wholesaleProgramLabel = (ref, programLookup) => {
  const type = String(ref?.program_type || "").trim();
  const id = String(ref?.program_id || "").trim();
  if (!type || !id) return "—";
  const program = programLookup?.get(`${type}:${id}`);
  if (program) return programOptionLabel(program);
  // Prefer any name fields the API may already include on the ref.
  const name =
    ref.program_display_name ||
    ref.programDisplayName ||
    ref.display_name ||
    ref.displayName ||
    ref.agreement_id ||
    ref.agreementId;
  if (name) {
    return programOptionLabel({
      program_type: type,
      program_display_name: name,
      id,
    });
  }
  return `${type} · ${id}`;
};

const buildSections = (customer, form, programLookup = new Map(), orgAccountEntries = []) => {
  const accounts =
    form?.payeeAccountNumbers?.length
      ? form.payeeAccountNumbers
      : getCustomerAccountNumbers(customer);
  const active = form?.active != null ? Boolean(form.active) : isCustomerActive(customer);
  const statusLabel =
    (typeof form?.status === "string" && form.status) ||
    (active ? "ACTIVE" : "INACTIVE");

  const overview = [
    {
      label: "Organization name",
      value: display(form?.organizationName || customer?.customer_name),
    },
  ];
  pushIf(overview, Boolean(form?.customerId), {
    label: "Customer ID",
    value: display(form.customerId),
  });
  const createdDate = resolveCreatedDate(form, customer);
  const createdBy = resolveCreatedBy(form, customer);
  overview.push({
    label: "Created",
    value: formatDate(createdDate),
  });
  overview.push({
    label: "Created by",
    value: display(createdBy || "—"),
  });
  overview.push({
    label: "Status",
    value: <StatusChip status={String(statusLabel).toUpperCase()} />,
  });
  pushIf(overview, Boolean(form?.orgDescription || customer?.orgdescription), {
    label: "Description",
    value: display(form?.orgDescription || customer?.orgdescription),
  });
  pushIf(overview, isSubOrganization(customer), {
    label: "Type",
    value: "Sub-organization",
  });

  const contact = [
    {
      label: "Primary contact",
      value: display(
        form?.primaryContactName ||
          customer?.orgprimaryname ||
          customer?.orgPrimaryName
      ),
    },
    {
      label: "Email",
      value: display(
        form?.primaryContactEmail ||
          customer?.orgprimaryemail ||
          customer?.orgPrimaryEmail
      ),
    },
    {
      label: "Phone",
      value: display(
        form?.phoneNumber || customer?.phonenumber || customer?.phoneNumber
      ),
    },
  ];

  const addressLines = [
    form?.orgAddress || customer?.orgaddress,
    [form?.orgCity || customer?.orgcity, form?.orgState || customer?.orgstate]
      .filter(Boolean)
      .join(", "),
    [form?.orgCountry || customer?.orgcountry, form?.orgZipcode || customer?.orgzip]
      .filter(Boolean)
      .join(" "),
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  const address = [
    {
      label: "Address",
      value: addressLines.length ? addressLines.join(", ") : "—",
    },
  ];

  const billingParts = [
    form?.orgBillingName,
    form?.orgBillingEmail,
    form?.orgBillingAddress,
    [form?.orgBillingCity, form?.orgBillingState].filter(Boolean).join(", "),
    [form?.orgBillingCountry, form?.orgBillingZip].filter(Boolean).join(" "),
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  const billing = [];
  pushIf(billing, billingParts.length > 0, {
    label: "Billing contact",
    value: billingParts.join(", "),
  });

  const linkedItems = buildLinkedAccountItems(
    customer,
    form,
    accounts,
    orgAccountEntries
  );
  const accountsSection = [
    {
      label: linkedItems.length
        ? `Selected Accounts (${linkedItems.length})`
        : "Selected Accounts",
      value: <AccountChips items={linkedItems} />,
    },
    {
      label: "Regions",
      value: form?.regions?.length ? form.regions.join(", ") : "—",
    },
  ];

  const commercial = [];
  pushIf(commercial, Boolean(form?.customerId), {
    label: "Customer code",
    value: display(form.customerId),
  });
  pushIf(commercial, Boolean(form?.startDate || form?.endDate), {
    label: "Contract term",
    value:
      form?.startDate || form?.endDate
        ? `${formatDate(form.startDate)} – ${formatDate(form.endDate)}`
        : "—",
  });
  pushIf(commercial, Boolean(form?.paymentTerms), {
    label: "Payment terms",
    value: display(form.paymentTerms),
  });
  pushIf(commercial, Boolean(form?.multiYearCommitment), {
    label: "Multi-year commitment",
    value: form.multiYearCommitment ? `$${form.multiYearCommitment}` : "—",
  });
  pushIf(commercial, Boolean(form?.commitmentDiscount), {
    label: "Discount",
    value: form.commitmentDiscount ? `${form.commitmentDiscount}%` : "—",
  });
  pushIf(commercial, Boolean(form?.pricingModel), {
    label: "Pricing model",
    value: display(form.pricingModel),
  });
  pushIf(commercial, Boolean(form?.serviceExclusions?.length), {
    label: "Service exclusions",
    value: (form?.serviceExclusions || []).join(", "),
  });
  pushIf(commercial, Boolean(form?.wholesalePrograms?.length), {
    label: "Wholesale programs",
    value: (form?.wholesalePrograms || [])
      .map((ref) => wholesaleProgramLabel(ref, programLookup))
      .join("; "),
  });
  pushIf(
    commercial,
    form?.commitmentMode === "annual" && Boolean(form?.years?.length),
    {
      label: "Annual commitment years",
      value: display(form?.years?.length),
    }
  );
  pushIf(
    commercial,
    form?.commitmentMode === "tiers" && Boolean(form?.tiers?.length),
    {
      label: "Volume discount tiers",
      value: display(form?.tiers?.length),
    }
  );

  return [
    { title: "Overview", rows: overview },
    { title: "Contact", rows: contact },
    { title: "Address", rows: address },
    { title: "Billing", rows: billing },
    { title: "Accounts", rows: accountsSection },
    { title: "Commercial terms", rows: commercial },
  ].filter((section) => section.rows.length > 0);
};

const baselineFormFromCustomer = (customer) => {
  if (!customer) return null;
  return {
    organizationName: customer.customer_name || "",
    customerOrgcode: customer.orgcode || "",
    organizationId: resolveOrganizationId(customer),
    primaryContactName: customer.orgprimaryname || customer.orgPrimaryName || "",
    primaryContactEmail: customer.orgprimaryemail || customer.orgPrimaryEmail || "",
    phoneNumber: customer.phonenumber || customer.phoneNumber || "",
    orgDescription: customer.orgdescription || customer.orgDescription || "",
    orgAddress: customer.orgaddress || customer.orgAddress || "",
    orgCity: customer.orgcity || customer.orgCity || "",
    orgState: customer.orgstate || customer.orgState || "",
    orgZipcode: customer.orgzip || customer.orgZip || "",
    orgCountry: customer.orgcountry || customer.orgCountry || "",
    payeeAccountNumbers: getCustomerAccountNumbers(customer),
    active: isCustomerActive(customer),
    createdBy: resolveCreatedBy(customer),
    createdDate: resolveCreatedDate(customer),
  };
};

const CustomerDetailsDialog = ({ open, onClose, customer }) => {
  const dispatch = useDispatch();
  const customerOrgAccountsSuccess = useSelector(
    (state) => state.customer?.customerOrgAccountsSuccess
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [programLookup, setProgramLookup] = useState(() => new Map());

  const organizationId = resolveOrganizationId(customer);

  const orgAccountEntries = useMemo(
    () => flattenOrgAccountEntries(customerOrgAccountsSuccess),
    [customerOrgAccountsSuccess]
  );

  useEffect(() => {
    if (!open) return;
    dispatch(getCustomerOrgAccounts());
  }, [open, dispatch]);

  useEffect(() => {
    if (!open || !customer) {
      setDetail(null);
      setError("");
      setLoading(false);
      setProgramLookup(new Map());
      return undefined;
    }

    if (organizationId == null) {
      setDetail(null);
      setError("");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setDetail(null);
    getOrganizationCustomer(organizationId)
      .then((payload) => {
        if (cancelled) return;
        const record =
          payload?.organization_id != null || payload?.orgcode || payload?.partnerCentral
            ? payload
            : payload?.data && typeof payload.data === "object"
              ? payload.data
              : null;
        if (!record) {
          setError("Unable to load customer details.");
          return;
        }
        const mapped = formFromOrganizationCustomer(record);
        const pc =
          record.partnerCentral && typeof record.partnerCentral === "object"
            ? record.partnerCentral
            : {};
        setDetail({
          ...mapped,
          status:
            pc.status ||
            (record.active === false ? "INACTIVE" : "ACTIVE"),
          createdBy: resolveCreatedBy(mapped, record, pc),
          createdDate: resolveCreatedDate(mapped, record, pc),
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "Unable to load customer details.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, customer, organizationId]);

  // Resolve wholesale program UUIDs → MSP-entered display names (same source as Add Customer).
  useEffect(() => {
    if (!open) {
      setProgramLookup(new Map());
      return undefined;
    }
    let cancelled = false;
    listPartnerPrograms({ statuses: ["DRAFT", "ACTIVE"] })
      .then((programs) => {
        if (cancelled) return;
        const next = new Map();
        (Array.isArray(programs) ? programs : []).forEach((program) => {
          const type = String(program?.program_type || "").trim();
          const id = String(program?.id || "").trim();
          if (type && id) next.set(`${type}:${id}`, program);
        });
        setProgramLookup(next);
      })
      .catch(() => {
        if (!cancelled) setProgramLookup(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const form = useMemo(() => {
    if (detail) return detail;
    return baselineFormFromCustomer(customer);
  }, [detail, customer]);

  const sections = useMemo(() => {
    if (!customer || !form) return [];
    return buildSections(customer, form, programLookup, orgAccountEntries);
  }, [customer, form, programLookup, orgAccountEntries]);

  const title =
    form?.organizationName || customer?.customer_name || customer?.orgcode || "Customer";
  const subtitleParts = [
    form?.customerId ? `ID ${form.customerId}` : null,
  ].filter(Boolean);

  return (
    <Dialog
      open={open && Boolean(customer)}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      aria-labelledby="customer-details-dialog-title"
      PaperProps={{
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          overflow: "hidden",
          maxWidth: 680,
          maxHeight: { xs: "92vh", sm: "88vh" },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          flexShrink: 0,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id="customer-details-dialog-title"
            sx={{
              fontWeight: 700,
              fontSize: "1.0625rem",
              color: AH_WHITE,
              lineHeight: 1.3,
            }}
          >
            Customer Details
          </Typography>
          <Typography
            sx={{
              mt: 0.25,
              fontSize: 12,
              color: AH_WHITE,
              opacity: 0.92,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
            {subtitleParts.length ? ` · ${subtitleParts.join(" · ")}` : ""}
          </Typography>
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          disabled={loading && !form}
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: loading && !form ? "default" : "pointer",
            flexShrink: 0,
          }}
        >
          ×
        </Box>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2.5,
          backgroundColor: AH_WHITE,
          flex: "1 1 auto",
          overflowY: "auto",
        }}
      >
        {loading && !form ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} sx={{ color: AH_ACCENT }} />
          </Box>
        ) : error && !form ? (
          <Typography sx={{ fontSize: 13, color: "#d32f2f" }} role="alert">
            {error}
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gap: 1.75 }}>
            {error ? (
              <Typography sx={{ fontSize: 12, color: "#d32f2f" }} role="alert">
                {error} Showing available summary details.
              </Typography>
            ) : null}
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={14} sx={{ color: AH_MUTED }} />
                <Typography sx={{ fontSize: 12, color: AH_MUTED }}>
                  Loading full customer details…
                </Typography>
              </Box>
            ) : null}
            {sections.map((section) => (
              <DetailSection
                key={section.title}
                title={section.title}
                rows={section.rows}
              />
            ))}
          </Box>
        )}
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          px: 3,
          py: 2,
          borderTop: `1px solid ${AH_BORDER}`,
          backgroundColor: "#f8fafc",
          flexShrink: 0,
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          disabled={loading && !form}
          sx={{
            ...outlinedButtonSx,
            backgroundColor: "#74788d",
            color: AH_WHITE,
            borderColor: "#74788d",
            "&:hover": {
              backgroundColor: "#686c7f",
              borderColor: "#686c7f",
              boxShadow: "none",
            },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

CustomerDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
};

export default CustomerDetailsDialog;
